import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Box, Card, CardContent, CardMedia, Chip, Container, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import useWPPosts from "../hooks/useWPPosts";
import useWPPage from "../hooks/useWPPage";
import useLanguage from "../hooks/useLanguage";
import Loading from "../components/Shared/Loading";
import NotFound from "../components/Shared/NotFound";
import PageLayout from "../components/Shared/PageLayout";
import Footer from "../components/Shared/Footer";
import Header from "../components/Shared/Header";
import Kunde1Header from "../customers/kunde1/components/Header";
import Kunde2Header from "../customers/kunde2/components/Header";
import {
  getContentVisibilityLevel,
  isContentListed,
  isContentVisibleForAccess
} from "../content/contentClient";
import { getCustomerKey } from "../utils/domain";
import SeoHead from "../components/Shared/SeoHead";
import { getBlogBaseSlug, getBlogItemPath } from "../utils/blogRoutes";

const getHeaderForCustomer = () => {
  const key = getCustomerKey();
  if (key === "kunde1") return <Kunde1Header />;
  if (key === "kunde2") return <Kunde2Header />;
  return <Header />;
};

const getFeaturedImage = (post: { _embedded?: any }) =>
  post?._embedded?.["wp:featuredmedia"]?.[0]?.media_details?.sizes?.large?.source_url ||
  post?._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
  "";

type WPTerm = {
  id?: number;
  taxonomy?: string;
  name?: string;
  slug?: string;
};

const getCategories = (post: { _embedded?: any }): WPTerm[] => {
  const termGroups = (post?._embedded?.["wp:term"] as WPTerm[][]) || [];
  const categoryGroup = termGroups.find((group) =>
    group.some((term) => term.taxonomy === "category")
  );
  return categoryGroup || [];
};

const stripHtml = (value: string) => value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

const truncatePreview = (value: string, maxChars = 150) => {
  const text = stripHtml(value || "");
  if (text.length <= maxChars) return text;
  const sliced = text.slice(0, maxChars + 1);
  const lastSpace = sliced.lastIndexOf(" ");
  const safe = lastSpace > 0 ? sliced.slice(0, lastSpace) : text.slice(0, maxChars);
  return `${safe.trim()} [...]`;
};

export default function Blog() {
  const { lang } = useLanguage();
  const { posts, loading, error } = useWPPosts(12, lang);
  const listedPosts = posts.filter((post) => isContentListed(post));
  const blogBaseSlug = getBlogBaseSlug(lang);
  const { page } = useWPPage(blogBaseSlug, lang);
  const visuallyHiddenSx = {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0
  };
  const introHtml = page?.excerpt?.rendered || "";
  const acf = page?.acf || {};
  const pageTitle = page?.title?.rendered || (lang === "en" ? "News" : "Blog");
  const seoTitle = acf.seo_title || pageTitle;
  const seoDescription = acf.seo_description || introHtml.replace(/<[^>]+>/g, "");
  const ogTitle = acf.og_title || seoTitle;
  const ogDescription = acf.og_description || seoDescription;
  const ogImage = acf.og_image || "";
  const canonicalUrl = acf.canonical_url || (typeof window !== "undefined" ? window.location.href : "");
  const robots = acf.robots || "index";
  const showH1 = acf.show_h1 ?? true;
  const hideH1Visually = acf.hide_h1_visually ?? false;
  const renderVisibleH1 = showH1 && !hideH1Visually;
  const renderHiddenH1 = hideH1Visually;

  if (loading) return <Loading message="Blog wird geladen..." />;
  if (error || !listedPosts.length) return <NotFound message="Keine Beitraege gefunden." />;

  return (
    <PageLayout header={getHeaderForCustomer()} footer={<Footer />}>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        ogTitle={ogTitle}
        ogDescription={ogDescription}
        ogImage={ogImage}
        canonicalUrl={canonicalUrl}
        robots={robots}
      />
      <Box component="main" sx={{ py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Stack spacing={2} sx={{ mb: 4 }}>
            {renderHiddenH1 ? (
              <Typography variant="h1" component="h1" sx={visuallyHiddenSx}>
                <span dangerouslySetInnerHTML={{ __html: pageTitle }} />
              </Typography>
            ) : null}
            {renderVisibleH1 ? <Typography variant="h1" dangerouslySetInnerHTML={{ __html: pageTitle }} /> : null}
            {introHtml ? (
              <Box sx={{ color: "text.secondary" }} dangerouslySetInnerHTML={{ __html: introHtml }} />
            ) : null}
          </Stack>
          <Box
            sx={{
              display: "grid",
              gap: 3,
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }
            }}
          >
            {listedPosts.map((post) => {
              const image = getFeaturedImage(post);
              const categories = getCategories(post);
              const visibility = getContentVisibilityLevel(post);
              const isLocked = !isContentVisibleForAccess(post, []);
              const previewText =
                isLocked && post.acf?.access_teaser_message
                  ? post.acf.access_teaser_message
                  : truncatePreview(post.excerpt.rendered, 150);
              const author = post?._embedded?.author?.[0]?.name || "";
              const date = new Date(post.date);
              const dateLabel = Number.isNaN(date.getTime())
                ? ""
                : date.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
              return (
                <Card
                  key={post.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 180ms ease, box-shadow 180ms ease",
                    "&:hover .blog-card-media": { transform: "scale(1.04)" },
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 20px 45px rgba(11, 31, 39, 0.15)"
                    }
                  }}
                >
                  {image ? (
                    <Box sx={{ overflow: "hidden" }}>
                      <CardMedia
                        component="img"
                        height="180"
                        image={image}
                        alt={post.title.rendered}
                        className="blog-card-media"
                        sx={{ transition: "transform 220ms ease" }}
                      />
                    </Box>
                  ) : null}
                  <CardContent>
                    <Stack sx={{ height: "100%" }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                      {isLocked ? (
                        <Chip
                          icon={<LockOutlinedIcon />}
                          label={visibility === "private" ? "Privat" : "Premium"}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      ) : null}
                      {categories.slice(0, 2).map((category) => (
                        <Chip
                          key={category.id || category.slug}
                          label={category.name}
                          size="small"
                          component={Link}
                          to={getBlogItemPath(category.slug || "", lang)}
                          clickable
                        />
                      ))}
                    </Stack>
                    <Stack spacing={0.5} sx={{ mb: 1 }}>
                      <Typography
                        variant="h5"
                        component={Link}
                        to={getBlogItemPath(post.slug, lang)}
                        sx={{
                          textDecoration: "none",
                          color: "text.primary",
                          display: "inline-block",
                          transition: "color 180ms ease",
                          "&:hover": { color: "primary.main" }
                        }}
                        dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {[dateLabel, author].filter(Boolean).join(" - ")}
                      </Typography>
                    </Stack>
                    <Typography sx={{ mt: 1, color: "text.secondary" }}>
                      {previewText}
                    </Typography>
                    <Box sx={{ mt: "auto", pt: 2, display: "flex", justifyContent: "flex-end" }}>
                      <Typography
                        component={Link}
                        to={getBlogItemPath(post.slug, lang)}
                        sx={{
                          textDecoration: "none",
                          color: "primary.main",
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5
                        }}
                      >
                        {isLocked ? "Mehr erfahren" : "Mehr lesen"} <span aria-hidden="true">-&gt;</span>
                      </Typography>
                    </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Container>
      </Box>
    </PageLayout>
  );
}
